import { useTheme } from "@mui/material";
import ImageResize from "tiptap-extension-resize-image";
import { useEditor, EditorContent } from "@tiptap/react";
import { RichTextEditorProvider } from "mui-tiptap";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Code } from "@tiptap/extension-code";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Color } from "@tiptap/extension-color";
import { Document } from "@tiptap/extension-document";
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { FontFamily } from "@tiptap/extension-font-family";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Highlight } from "@tiptap/extension-highlight";
import { History } from "@tiptap/extension-history";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Strike } from "@tiptap/extension-strike";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Text } from "@tiptap/extension-text";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { uploadFile } from "../../services/blog";
import Table from "@tiptap/extension-table";
import {
  MenuButtonAddTable,
  MenuButtonBold,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTaskList,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign,
  isTouchDevice,
  FontSize,
  HeadingWithAnchor,
  LinkBubbleMenuHandler,
  ResizableImage,
} from "mui-tiptap";

import { useState, useCallback } from "react";
import { Tooltip, Popover } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";

const MenuBar = ({ editor }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const hide = () => {
    setOpen(false);
  };
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };
  const [openList, setOpenList] = useState(false);

  const handleOpenListChange = (newOpen) => {
    setOpenList(newOpen);
  };

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  const uploadFileImage = async (file) => {
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      const res = await uploadFile(formData);

      return res.path;
    }
    return "File null";
  };

  const handleUploadFilesImage = async (files) => {
    let link = new Map();

    for (const file of files) {
      const link_file = await uploadFileImage(file);

      link.set(file, link_file);
    }

    return link;
  };

  const onUploadFilesCustom = async (files) => {
    // console.log("Custom");
    const fileLinksMap = await handleUploadFilesImage(files); // Wait for upload results and links
    return files.map((file) => ({
      src: fileLinksMap.get(file) || "", // Get link from Map or empty string if unavailable
      alt: `IMG_${file.name}`, // Create alt text from file name
    }));
  };

  return (
    <MenuControlsContainer className="">
      <MenuSelectFontFamily options={[{ label: "Cabin", value: "Cabin" }]} />

      <MenuDivider />

      <MenuSelectHeading />

      <MenuDivider />

      <MenuSelectFontSize />

      <MenuDivider />

      <MenuButtonBold />

      <MenuButtonItalic />

      <MenuButtonUnderline />

      <MenuButtonStrikethrough />

      <MenuButtonSubscript />

      <MenuButtonSuperscript />

      <MenuDivider />

      <MenuButtonTextColor
        defaultTextColor={theme.palette.text.primary}
        PopperProps={{
          style: { zIndex: `${theme.zIndex.modal + 999} !important` },
        }}
      />

      <MenuButtonHighlightColor
        PopperProps={{
          style: { zIndex: `${theme.zIndex.modal + 999} !important` },
        }}
      />

      <MenuDivider />

      <div className="flex gap-4">
        <Tooltip title="Set link">
          <button
            onClick={setLink}
            className={editor.isActive("link") ? "is-active" : ""}
          >
            <Icon
              icon={"material-symbols:link"}
              className="text-[#6d6d6d]"
            ></Icon>
          </button>
        </Tooltip>

        <Tooltip title=<p className="text-white font-thin">Unlink</p>>
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editor.isActive("link")}
          >
            <Icon icon={"ion:unlink-outline"} className="text-[#6d6d6d]"></Icon>
          </button>
        </Tooltip>
      </div>

      <MenuDivider />

      <MenuSelectTextAlign />

      <MenuDivider />

      <Popover
        content={
          <div className="flex justify-around ">
            <Tooltip title="Add list">
              <button
                style={{ cursor: "pointer" }}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={editor.isActive("orderedList") ? "is-active" : ""}
              >
                <Icon
                  icon={"stash:list-add"}
                  className="text-[#6d6d6d] h-6 w-6"
                ></Icon>
              </button>
            </Tooltip>

            <Tooltip title="Split">
              <button
                className="cursor-pointer"
                onClick={() =>
                  editor.chain().focus().splitListItem("listItem").run()
                }
                disabled={!editor.can().splitListItem("listItem")}
              >
                <Icon
                  className="text-[#6d6d6d] h-6 w-6"
                  icon={"system-uicons:split"}
                ></Icon>
              </button>
            </Tooltip>
            <Tooltip title="Sink">
              <button
                className="cursor-pointer"
                onClick={() =>
                  editor.chain().focus().sinkListItem("listItem").run()
                }
                disabled={!editor.can().sinkListItem("listItem")}
              >
                <Icon
                  icon={"solar:list-down-bold"}
                  className="text-[#6d6d6d] h-6 w-6"
                ></Icon>
              </button>
            </Tooltip>
            <Tooltip title="Lift">
              <button
                className="cursor-pointer"
                onClick={() =>
                  editor.chain().focus().liftListItem("listItem").run()
                }
                disabled={!editor.can().liftListItem("listItem")}
              >
                <Icon
                  icon={"solar:list-up-bold"}
                  className="text-[#6d6d6d] h-6 w-6"
                ></Icon>
              </button>
            </Tooltip>
          </div>
        }
        title="Ordered List "
        trigger="click"
        open={openList}
        onOpenChange={handleOpenListChange}
      >
        <button className="">
          <Icon className=" text-[#6d6d6d]" icon={"fa-solid:list-ul"}></Icon>
        </button>
      </Popover>

      <MenuButtonTaskList />

      {isTouchDevice() && (
        <>
          <MenuButtonIndent />

          <MenuButtonUnindent />
        </>
      )}

      {/* <MenuDivider /> */}

      {/* <MenuButtonBlockquote /> */}

      <MenuDivider />

      <MenuButtonCode />

      <MenuButtonCodeBlock />

      <MenuDivider />

      <MenuButtonImageUpload
        onUploadFiles={(files) => onUploadFilesCustom(files)}
      />

      <MenuDivider />

      <MenuButtonHorizontalRule />

      <Popover
        content={
          <div className=" flex-col flex gap-2 ">
            <div className="flex  gap-4 ">
              <Tooltip title="Add Table">
                <button
                  className=" rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  }
                >
                  <Icon
                    icon={"mdi:table-add"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
              <Tooltip title="Add Column Before">
                <button
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                >
                  <Icon
                    icon={"mdi:table-column-add-before"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>

              <Tooltip title="Add Column After">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                >
                  <Icon
                    icon={"mdi:table-column-add-after"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
              <Tooltip title="Delete Column">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                >
                  <Icon
                    icon={"hugeicons:column-delete"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
            </div>

            <div className="flex  gap-4 ">
              <Tooltip title="Add row before">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                >
                  <Icon
                    icon={"mdi:table-row-add-before"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
              <Tooltip title="Add Row After">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                >
                  <Icon
                    icon={"mdi:table-row-add-after"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
              <Tooltip title="Delete Row">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().deleteRow().run()}
                >
                  <Icon
                    icon={"hugeicons:row-delete"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
              <Tooltip title="Delete Table">
                <button
                  className="rounded-md border-[1px] border-[#6d6d6d] p-1"
                  onClick={() => editor.chain().focus().deleteTable().run()}
                >
                  <Icon
                    icon={"flowbite:delete-table-outline"}
                    className="h-5 w-5 text-[#6d6d6d]"
                  ></Icon>
                </button>
              </Tooltip>
            </div>
            <div className="flex  gap-4 "></div>
          </div>
        }
        title="Table"
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
      >
        <MenuButtonAddTable />
      </Popover>

      <MenuDivider />

      <MenuButtonRemoveFormatting />

      <MenuDivider />

      <MenuButtonUndo />
      <MenuButtonRedo />
    </MenuControlsContainer>
  );
};

const Editor = ({ setDescription, content }) => {
  const CustomSubscript = Subscript.extend({
    excludes: "superscript",
  });

  const CustomSuperscript = Superscript.extend({
    excludes: "subscript",
  });
  const extensions = [
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,

    CodeBlock,
    Document,
    HardBreak,

    // OrderedList,
    // OrderedList.configure({
    //   itemTypeName: "listItem",
    //   keepMarks: true,
    //   keepAttributes: true,
    // }),
    // ListItem,
    BulletList,
    OrderedList,
    ListItem,
    Paragraph,
    CustomSubscript,
    CustomSuperscript,
    Text,

    Bold,
    // Blockquote,

    Code,
    Italic,
    Underline,
    Strike,
    Link.configure({
      inclusive: false,

      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
    }),
    LinkBubbleMenuHandler,

    Gapcursor,
    HeadingWithAnchor,
    TextAlign.configure({
      types: ["heading", "paragraph", "image"],
    }),
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    Highlight.configure({ multicolor: true }),
    HorizontalRule,

    ResizableImage,

    Dropcursor,

    TaskList,
    TaskItem.configure({
      nested: true,
    }),

    History,
    ImageResize,
  ];
  const editor = useEditor({
    extensions: extensions,
    content: content,

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setDescription(html);
    },
  });

  return (
    <RichTextEditorProvider editor={editor}>
      <div className="textEditor ">
        <MenuBar editor={editor} />

        <div className="max-h-[500px] pv:max-ph:max-h-[300px] overflow-y-auto w-full">
          <EditorContent editor={editor} className="w-full text-primary" />
        </div>
      </div>
    </RichTextEditorProvider>
  );
};
export default Editor;
